import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { getCategoryBySlug } from '@/mocks/categories';

const SITE_URL = 'https://www.krcomic.com';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4">
          <div className="w-16 h-16 flex items-center justify-center">
            <i className="ri-error-warning-line text-foreground-300 text-3xl" />
          </div>
          <p className="text-foreground-500">漫画未找到</p>
          <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(comic.category);
  const relatedComics = getRelatedComics(comic, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[0]?.date || '',
    dateModified: comic.updatedAt,
    image: comic.coverImage,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `${SITE_URL}/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `${SITE_URL}/comic/${comic.id}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${category?.name || ''}韩国漫画免费在线阅读 - 韩漫大全`}
        description={`${comic.title}：${comic.description.slice(0, 120)}。韩漫大全为您提供${comic.title}全章节免费在线阅读，高清画质，每日更新。`}
        keywords={`${comic.title},韩国漫画,免费韩漫,在线阅读,${comic.tags.join(',')}`}
        canonicalUrl={`${SITE_URL}/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${comic.category}` }] : []),
              { label: comic.title },
            ]}
          />

          <article className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-16">
            <div className="lg:w-[320px] flex-shrink-0">
              <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4] sticky top-24">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover"
                  title={`${comic.title} - 韩国漫画封面`}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500" />
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">/ 5.0</span>
                </div>
                <span className="text-sm text-foreground-500">
                  {comic.views.toLocaleString()} 次阅读
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-8 border border-background-200/70">
                <h2 className="text-sm font-semibold text-foreground-900 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters.length} 话</span>
                </h2>
                <div className="bg-background-100 rounded-xl border border-background-200/70 divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
                  {comic.chapters.map(chapter => (
                    <div
                      key={chapter.number}
                      className="flex items-center justify-between px-5 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-foreground-500 font-mono whitespace-nowrap">
                          CH.{String(chapter.number).padStart(3, '0')}
                        </span>
                        <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-4">{chapter.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedComics.length > 0 && (
            <section>
              <div className="flex items-end justify-between mb-6">
                <h2 className="text-xl font-heading font-bold text-foreground-900">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map(rc => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-200">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        title={`${rc.title} - 相关推荐韩国漫画`}
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                      {rc.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs" />
                      </div>
                      <span className="text-xs text-foreground-500">{rc.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}